// src/firebaseClient.js
// ANDROID-OPTIMIZED Firebase initialization
// Enhanced for better Android WebView compatibility and debugging

import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  initializeAuth,
  signInAnonymously,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  linkWithCredential,
  EmailAuthProvider,
  indexedDBLocalPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
};

let app;
if (!getApps().length) {
  app = initializeApp(firebaseConfig);
  console.log('[Firebase] App initialized');
} else {
  app = getApps()[0];
  console.log('[Firebase] Using existing app instance');
}

// ===== ANDROID-OPTIMIZED AUTH SETUP =====
// On Android WebView (Capacitor), indexedDBLocalPersistence is more reliable
let _auth;
try {
  _auth = initializeAuth(app, {
    persistence: indexedDBLocalPersistence
  });
  console.log('[Firebase Auth] Initialized with IndexedDB persistence (Android-optimized)');
} catch (err) {
  // If already initialized (hot reload) or not supported, fall back gracefully
  console.warn('[Firebase Auth] initializeAuth failed, using getAuth():', err?.message);
  _auth = getAuth(app);
}
export const auth = _auth;

// Initialize Firestore (must be before functions that use db)
export const db = getFirestore(app);
console.log('[Firebase] Firestore initialized');

// Initialize Storage
export const storage = getStorage(app);
console.log('[Firebase Storage] Bucket:', firebaseConfig.storageBucket);

// ===== IMPROVED AUTH READY TRACKING =====
let _authReadyResolve;
let _authReadyReject;
let _isAuthReady = false;

// Create a NEW promise each time (not reused)
function createAuthReadyPromise() {
  return new Promise((resolve, reject) => {
    if (_isAuthReady && auth.currentUser) {
      // Already ready, resolve immediately
      resolve(auth.currentUser);
    } else {
      // Wait for auth state change
      _authReadyResolve = resolve;
      _authReadyReject = reject;
    }
  });
}

// ANDROID FIX: Increased default timeout to 10s (was 4s)
export async function waitForAuthReady(timeoutMs = 10000) {
  console.log(`[Firebase Auth] Waiting for auth ready (timeout: ${timeoutMs}ms)...`);
  
  // If already authenticated, return immediately
  if (auth.currentUser) {
    console.log('[Firebase Auth] ✅ Already authenticated:', auth.currentUser.uid);
    return auth.currentUser;
  }
  
  // Create a fresh promise for this wait
  const authPromise = createAuthReadyPromise();
  
  // Create timeout promise
  const timeoutPromise = new Promise((_, reject) =>
    setTimeout(() => {
      console.warn(`[Firebase Auth] ⏱️ Timeout after ${timeoutMs}ms`);
      reject(new Error('auth-wait-timeout'));
    }, timeoutMs)
  );
  
  try {
    const user = await Promise.race([authPromise, timeoutPromise]);
    console.log('[Firebase Auth] ✅ Auth ready:', user?.uid);
    return user;
  } catch (err) {
    console.warn('[Firebase Auth] ⚠️ Auth wait failed:', err?.message);
    
    // ANDROID FIX: Last resort - try one more anonymous sign-in
    try {
      console.log('[Firebase Auth] Attempting emergency anonymous sign-in...');
      const credential = await signInAnonymously(auth);
      console.log('[Firebase Auth] ✅ Emergency sign-in successful:', credential.user?.uid);
      return credential.user;
    } catch (signInErr) {
      console.error('[Firebase Auth] ❌ Emergency sign-in failed:', signInErr?.message);
      return null;
    }
  }
}

// Always-available helper: guarantees we are signed in before Storage writes
export async function ensureAuth() {
  if (auth.currentUser) {
    console.log('[Firebase Auth] Already authenticated');
    return auth.currentUser;
  }
  
  try {
    console.log('[Firebase Auth] Signing in anonymously...');
    const credential = await signInAnonymously(auth);
    console.log('[Firebase Auth] ✅ Anonymous sign-in successful:', credential.user?.uid);
    return credential.user;
  } catch (err) {
    console.error('[Firebase Auth] ❌ Anonymous sign-in failed:', err?.message || err);
    return null;
  }
}

// ===== USER MANAGEMENT (Subscription Support) =====

/**
 * Create a new authenticated user account
 * This is called when a free user upgrades to Pro
 */
export async function createUserAccount(email, password) {
  try {
    console.log('[Firebase Auth] Creating user account:', email);
    
    // Check if currently signed in anonymously
    const currentUser = auth.currentUser;
    const wasAnonymous = currentUser?.isAnonymous;
    
    if (wasAnonymous) {
      // Link anonymous account to email/password
      console.log('[Firebase Auth] Linking anonymous account to email...');
      const credential = EmailAuthProvider.credential(email, password);
      const userCredential = await linkWithCredential(currentUser, credential);
      console.log('[Firebase Auth] ✅ Anonymous account linked successfully');
      
      // Create Firestore user doc
      await initializeUserDoc(userCredential.user.uid, email);
      
      return userCredential.user;
    } else {
      // Create new account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      console.log('[Firebase Auth] ✅ New account created');
      
      // Create Firestore user doc
      await initializeUserDoc(userCredential.user.uid, email);
      
      return userCredential.user;
    }
  } catch (error) {
    console.error('[Firebase Auth] ❌ Account creation failed:', error.message);
    throw error;
  }
}

/**
 * Sign in existing user
 */
export async function signIn(email, password) {
  try {
    console.log('[Firebase Auth] Signing in:', email);
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    console.log('[Firebase Auth] ✅ Sign in successful');
    return userCredential.user;
  } catch (error) {
    console.error('[Firebase Auth] ❌ Sign in failed:', error.message);
    throw error;
  }
}

/**
 * Sign out current user
 */
export async function signOut() {
  try {
    await firebaseSignOut(auth);
    console.log('[Firebase Auth] ✅ Signed out');
    
    // Sign back in anonymously for free tier
    await signInAnonymously(auth);
    console.log('[Firebase Auth] ✅ Signed back in anonymously');
  } catch (error) {
    console.error('[Firebase Auth] ❌ Sign out failed:', error.message);
    throw error;
  }
}

// ===== FIRESTORE USER MANAGEMENT =====

/**
 * Initialize user document in Firestore
 */
async function initializeUserDoc(userId, email) {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      // Get current early adopter count
      const count = await getEarlyAdopterCount();
      const isEarlyAdopter = count < parseInt(process.env.REACT_APP_EARLY_ADOPTER_LIMIT || '1000');
      
      await setDoc(userRef, {
        email,
        subscriptionTier: 'free',
        subscriptionStatus: 'none',
        earlyAdopter: isEarlyAdopter,
        earlyAdopterNumber: isEarlyAdopter ? count + 1 : null,
        albumsCreatedThisMonth: 0,
        monthlyLimitResetAt: getNextMonthTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      
      console.log('[Firestore] ✅ User doc created:', userId);
    }
  } catch (error) {
    console.error('[Firestore] ❌ Failed to initialize user doc:', error.message);
    throw error;
  }
}

/**
 * Get user subscription data
 */
export async function getUserSubscription(userId) {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      return userDoc.data();
    }
    return null;
  } catch (error) {
    console.error('[Firestore] ❌ Failed to get user subscription:', error.message);
    return null;
  }
}

/**
 * Update user subscription status (called by Stripe webhook)
 */
export async function updateUserSubscription(userId, subscriptionData) {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      ...subscriptionData,
      updatedAt: serverTimestamp(),
    });
    console.log('[Firestore] ✅ User subscription updated:', userId);
  } catch (error) {
    console.error('[Firestore] ❌ Failed to update subscription:', error.message);
    throw error;
  }
}

/**
 * Get current early adopter count
 */
export async function getEarlyAdopterCount() {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('earlyAdopter', '==', true));
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error('[Firestore] ❌ Failed to get early adopter count:', error.message);
    return 0;
  }
}

/**
 * Increment album creation count for user
 */
export async function incrementAlbumCount(userId) {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const data = userDoc.data();
      const now = Date.now();
      
      // Check if we need to reset monthly count
      if (now > data.monthlyLimitResetAt?.toMillis()) {
        await updateDoc(userRef, {
          albumsCreatedThisMonth: 1,
          monthlyLimitResetAt: getNextMonthTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } else {
        await updateDoc(userRef, {
          albumsCreatedThisMonth: (data.albumsCreatedThisMonth || 0) + 1,
          updatedAt: serverTimestamp(),
        });
      }
    }
  } catch (error) {
    console.error('[Firestore] ❌ Failed to increment album count:', error.message);
  }
}

// ===== UTILITY FUNCTIONS =====

function getNextMonthTimestamp() {
  const now = new Date();
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return nextMonth;
}

// ===== IMPROVED AUTH STATE LISTENER =====
// Eager anonymous sign-in and resolve readiness when we have a user
let _signInAttempted = false;

onAuthStateChanged(auth, (user) => {
  if (!user && !_signInAttempted) {
    // No user yet, attempt anonymous sign-in
    console.log('[Firebase Auth] No user detected, signing in anonymously...');
    _signInAttempted = true;
    
    signInAnonymously(auth)
      .then(() => {
        console.log('[Firebase Auth] ✅ Initial anonymous sign-in successful');
      })
      .catch((err) => {
        console.error('[Firebase Auth] ❌ Initial anonymous sign-in failed:', err?.message);
        // Reject any pending auth waits
        if (_authReadyReject) {
          _authReadyReject(new Error('initial-sign-in-failed'));
          _authReadyReject = null;
        }
      });
  } else if (user) {
    // User authenticated - resolve any pending waiters
    console.log('[Firebase Auth] ✅ User authenticated:', user.uid, user.isAnonymous ? '(anonymous)' : '(registered)');
    _isAuthReady = true;
    
    if (_authReadyResolve) {
      _authReadyResolve(user);
      _authReadyResolve = null;
      _authReadyReject = null;
    }
  }
});

// ===== ANDROID-SPECIFIC DIAGNOSTICS =====
export function getFirebaseStatus() {
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isWebView = /wv/.test(navigator.userAgent);
  
  const status = {
    isAndroid,
    isWebView,
    isAuthenticated: !!auth.currentUser,
    userId: auth.currentUser?.uid || null,
    authReady: _isAuthReady,
    storageConfigured: !!storage,
    storageBucket: firebaseConfig.storageBucket,
  };
  
  console.log('[Firebase Status]', status);
  return status;
}

// ANDROID FIX: Expose a way to manually trigger sign-in if needed
export async function forceSignIn() {
  console.log('[Firebase Auth] Force sign-in requested...');
  try {
    const credential = await signInAnonymously(auth);
    console.log('[Firebase Auth] ✅ Force sign-in successful:', credential.user?.uid);
    return credential.user;
  } catch (err) {
    console.error('[Firebase Auth] ❌ Force sign-in failed:', err?.message);
    throw err;
  }
}

// ============================================
// FEEDBACK
// ============================================

/**
 * Save user feedback to Firestore
 * @param {Object} feedback - The feedback object
 * @param {string} feedback.message - The feedback message
 * @param {string} feedback.category - Category: 'bug', 'idea', 'other'
 * @param {string} [feedback.email] - Optional email for follow-up
 * @returns {Promise<string>} - The document ID of the saved feedback
 */
export async function saveFeedback({ message, category, email }) {
  try {
    const user = auth.currentUser;
    const platform = window?.Capacitor?.getPlatform?.() || 'web';
    const isNative = !!window?.Capacitor?.isNativePlatform?.();

    const feedbackDoc = {
      message,
      category: category || 'other',
      email: email || user?.email || null,
      userId: user?.uid || null,
      isAnonymous: user?.isAnonymous ?? true,
      platform,
      isNative,
      userAgent: navigator.userAgent,
      appVersion: process.env.REACT_APP_VERSION || 'unknown',
      createdAt: serverTimestamp(),
      status: 'new' // For tracking: new, reviewed, resolved
    };

    const docRef = await addDoc(collection(db, 'feedback'), feedbackDoc);
    console.log('[Feedback] ✅ Saved feedback:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('[Feedback] ❌ Failed to save feedback:', error);
    throw error;
  }
}

// Detect platform on startup
if (typeof window !== 'undefined') {
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isWebView = /wv/.test(navigator.userAgent);

  if (isAndroid) {
    console.log('[Firebase] 🤖 Running on Android', isWebView ? '(WebView)' : '(Browser)');
  }
}